// Dragging a gantt bar or its edge: the shift snaps to whole days, the timeline scrolls at its edges, Esc cancels.
// Only mouse and pen drag: a finger on a phone scrolls the timeline, and a tap opens the record.

import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import type { Id } from '@confdeck/core-shared';
import { snapDays, type DragMode } from './scale.ts';

export type Drag = { id: Id; mode: DragMode; delta: number; moved: boolean };
const THRESHOLD = 4, EDGE = 48, SPEED = 14;

/** `inset` — the width of the list column that covers the left of the scroller: the left scroll zone starts after it. */
export function useBarDrag(scroller: RefObject<HTMLDivElement | null>, px: number, inset: number, onDrop: (id: Id, mode: DragMode, delta: number) => void) {
  const [drag, setDrag] = useState<Drag | null>(null);
  const st = useRef<{ id: Id; mode: DragMode; x0: number; s0: number; x: number; moved: boolean; delta: number; raf: number } | null>(null);
  /** A drag ends with a click on the bar; this swallows it so the record does not open. */
  const dragged = useRef(false);
  const dropRef = useRef(onDrop);
  dropRef.current = onDrop;

  const update = useCallback(() => {
    const s = st.current, el = scroller.current;
    if (!s || !el) return;
    const dx = s.x - s.x0 + (el.scrollLeft - s.s0);
    if (!s.moved && Math.abs(dx) < THRESHOLD) return;
    const first = !s.moved;
    s.moved = true;
    const delta = snapDays(dx, px);
    if (delta !== s.delta || first) { s.delta = delta; setDrag({ id: s.id, mode: s.mode, delta, moved: true }); }
  }, [px, scroller]);

  // Near the left or right edge of the timeline the view scrolls on, and the bar follows the pointer.
  const tick = useCallback(() => {
    const s = st.current, el = scroller.current;
    if (!s || !el) return;
    const r = el.getBoundingClientRect();
    const v = s.x > r.right - EDGE ? SPEED : s.x < r.left + inset + EDGE ? -SPEED : 0;
    if (v && s.moved) { el.scrollLeft += v; update(); }
    s.raf = requestAnimationFrame(tick);
  }, [scroller, update, inset]);

  // Window listeners are attached in the pointerdown itself: an effect would come a frame late and miss a quick drag.
  const off = useRef<() => void>(() => {});
  const stop = useCallback((commit: boolean) => {
    const s = st.current;
    if (!s) return;
    off.current();
    cancelAnimationFrame(s.raf);
    st.current = null;
    setDrag(null);
    if (s.moved) {
      dragged.current = true;
      setTimeout(() => { dragged.current = false; }, 0);
      if (commit && s.delta) dropRef.current(s.id, s.mode, s.delta);
    }
  }, []);

  const start = useCallback((e: React.PointerEvent, id: Id, mode: DragMode) => {
    if (e.button !== 0 || e.pointerType === 'touch' || !scroller.current) return;
    e.stopPropagation();
    // No text selection while dragging; preventDefault also stops the button taking focus, so it is focused by hand:
    // after a click the arrows keep moving the bar (DESIGN.md «Гант»).
    e.preventDefault();
    (e.currentTarget as HTMLElement).closest('button')?.focus({ preventScroll: true, focusVisible: false } as FocusOptions);
    off.current();
    st.current = { id, mode, x0: e.clientX, s0: scroller.current.scrollLeft, x: e.clientX, moved: false, delta: 0, raf: 0 };
    st.current.raf = requestAnimationFrame(tick);
    const move = (ev: PointerEvent) => { if (st.current) { st.current.x = ev.clientX; update(); } };
    const up = () => stop(true);
    const key = (ev: KeyboardEvent) => { if (ev.key === 'Escape') stop(false); };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);
    window.addEventListener('pointercancel', up);
    window.addEventListener('keydown', key);
    off.current = () => {
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      window.removeEventListener('pointercancel', up);
      window.removeEventListener('keydown', key);
      off.current = () => {};
    };
    setDrag({ id, mode, delta: 0, moved: false });
  }, [scroller, tick, update, stop]);
  useEffect(() => () => off.current(), []);

  return { drag: drag?.moved ? drag : null, start, dragged };
}
